import { useState, useCallback, useEffect } from 'react';

/**
 * Currency Hook (Phase 1.2)
 *
 * Manages currency state, conversion, and persistence for the NFA application.
 */

export type Currency = 'GBP' | 'EUR' | 'USD';

export const CURRENCIES: Currency[] = ['GBP', 'EUR', 'USD'];

export const EXCHANGE_RATES: Record<Currency, number> = {
  GBP: 1,
  EUR: 1.17,
  USD: 1.27,
};

export const CURRENCY_SYMBOLS: Record<Currency, string> = {
  GBP: '£',
  EUR: '€',
  USD: '$',
};

const STORAGE_KEY = 'nfa-currency';

/**
 * Format a price value with the appropriate currency symbol
 */
export function formatPrice(value: number, currency: Currency): string {
  const symbol = CURRENCY_SYMBOLS[currency];
  const rounded = Math.round(value);
  return `${symbol}${rounded}`;
}

/**
 * Hook for managing currency state, conversion, and formatting
 */
export function useCurrency() {
  const [currency, setCurrencyState] = useState<Currency>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && CURRENCIES.includes(stored as Currency)) {
        return stored as Currency;
      }
    } catch {
      // localStorage might not be available
    }
    return 'GBP';
  });

  // Persist to localStorage whenever currency changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, currency);
    } catch {
      // localStorage might be full or disabled
    }
  }, [currency]);

  /**
   * Set the current currency
   */
  const setCurrency = useCallback((newCurrency: Currency) => {
    if (CURRENCIES.includes(newCurrency)) {
      setCurrencyState(newCurrency);
    }
  }, []);

  /**
   * Convert a GBP value to the current currency
   */
  const convert = useCallback(
    (gbpValue: number): number => {
      return gbpValue * EXCHANGE_RATES[currency];
    },
    [currency]
  );

  /**
   * Convert and format a GBP value to the current currency
   */
  const formatPriceFn = useCallback(
    (gbpValue: number): string => {
      const converted = convert(gbpValue);
      return formatPrice(converted, currency);
    },
    [currency, convert]
  );

  return {
    currency,
    setCurrency,
    convert,
    formatPrice: formatPriceFn,
    availableCurrencies: CURRENCIES,
    symbol: CURRENCY_SYMBOLS[currency],
    exchangeRate: EXCHANGE_RATES[currency],
  };
}
